import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound } from "lucide-react";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const strength = (() => {
    let s = 0;
    if (password.length >= 8) s++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) s++;
    if (/\d/.test(password)) s++;
    if (/[^A-Za-z0-9]/.test(password)) s++;
    return s;
  })();
  const strengthMeta = [
    { label: "Very weak", color: "bg-brand-terracotta" },
    { label: "Weak", color: "bg-brand-terracotta" },
    { label: "Fair", color: "bg-amber-500" },
    { label: "Good", color: "bg-brand-lease" },
    { label: "Strong", color: "bg-brand-emerald" },
  ][strength];

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    if (password !== confirm) { toast.error("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please log in with your new password.");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-5 py-16" data-testid="reset-password-page">
      <div className="bg-card border border-brand-line rounded-2xl p-8">
        <div className="w-12 h-12 rounded-xl bg-brand-emerald/10 flex items-center justify-center mb-5">
          <KeyRound className="w-6 h-6 text-brand-emerald" />
        </div>
        <h1 className="font-heading font-bold text-2xl text-brand-ink">Set a new password</h1>
        <p className="text-brand-muted text-sm mt-1 mb-6">Choose a strong password you don't use elsewhere.</p>

        {!token ? (
          <div className="rounded-xl bg-brand-terracotta/5 border border-brand-terracotta/20 text-brand-terracotta text-sm px-4 py-3" data-testid="reset-password-no-token">
            This reset link is invalid or incomplete. Please request a new one from the Forgot password page.
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>New password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="reset-password-new" className="mt-1.5" />
              {password && (
                <div className="mt-2" data-testid="password-strength">
                  <div className="flex gap-1">
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i < strength ? strengthMeta.color : "bg-brand-line"}`} />
                    ))}
                  </div>
                  <p className="text-xs mt-1 text-brand-muted">Password strength: <span className="font-medium text-brand-ink" data-testid="password-strength-label">{strengthMeta.label}</span></p>
                </div>
              )}
            </div>
            <div>
              <Label>Confirm new password</Label>
              <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="reset-password-confirm" className="mt-1.5" />
            </div>
            <Button type="submit" disabled={loading} data-testid="reset-password-submit" className="w-full rounded-full bg-brand-emerald hover:bg-brand-emeraldHover h-11">
              {loading ? "Updating..." : "Update password"}
            </Button>
          </form>
        )}

        <div className="mt-6 pt-5 border-t border-brand-line text-center">
          <Link to="/login" data-testid="reset-back-to-login" className="text-sm text-brand-emerald font-semibold hover:underline">← Back to login</Link>
        </div>
      </div>
    </div>
  );
}
